// @flow
import spinner from 'assets/spinner.svg'
import Spinner from 'components/Spinner'
import map from 'lodash/map'
import { useAddExports } from 'queries/exports/useAddExports'
import { useUpdateExports } from 'queries/exports/useUpdateExports'
import { useLoadFormLabels } from 'queries/forms/useLoadForms'
import { useLoadQuestions } from 'queries/questions/useLoadQuestions'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import Icons from 'ui/Icons'
import ModalDialog from 'ui/ModalDialog'
import SelectField from 'ui/molecules/SelectField'
import TextField from 'ui/molecules/TextField'
import PrimaryButton from 'ui/PrimaryButton'
import {
  exportNameSchema,
  formSchema,
  newExportQuestionListSchema,
  schemasValidate,
} from 'utils/schemas'

type Props = {
  onClose: () => void,
  selectedExport: any,
}

export default function ExportsCreateModal({
  onClose,
  selectedExport,
}: Props): React.Node {
  const history = useHistory()
  const [newExport, setNewExport] = React.useState<{
    name: string,
    form: string,
    questions: number[],
  }>(
    {
      name: selectedExport?.name || '',
      questions: selectedExport?.questions || [],
      form: selectedExport?.form?.id || '',
    } || {
      name: '',
      form: '',
      questions: [],
    }
  )
  const { data: forms, isLoading: isFormsLoading } = useLoadFormLabels()

  const {
    data: questionsResponse = [],
    isLoading: isQuestionsLoading,
    isFetching: isQuestionsFetching,
  } = useLoadQuestions({ formId: newExport.form })

  const questions = React.useMemo(() => {
    return questionsResponse?.filter(
      (q) => !['display_only', 'file_upload'].includes(q.type || '')
    )
  }, [questionsResponse])

  const isLoadingQuestions =
    !!newExport.form && (isQuestionsLoading || isQuestionsFetching)

  const { addExport } = useAddExports()
  const { updateExport } = useUpdateExports()

  const initialFormError = React.useRef({
    name: false,
    form: false,
    questions: false,
  }).current
  const initialFormErrorLabel = React.useRef({
    name: undefined,
    form: undefined,
    questions: undefined,
  }).current
  const [formError, setFormError] = React.useState(initialFormError)
  const [formErrorLabel, setFormErrorLabel] = React.useState(
    initialFormErrorLabel
  )
  const [isSubmitting, setSubmitting] = React.useState(false)

  const validateForm = () => {
    return schemasValidate(
      [
        ['name', newExport.name, exportNameSchema],
        ['form', newExport.form, formSchema],
        ['questions', newExport.questions, newExportQuestionListSchema],
      ],
      setFormError,
      setFormErrorLabel
    )
  }

  const onSave = async () => {
    try {
      setSubmitting(true)
      const isFormValid = await validateForm()
      if (!isFormValid) {
        setSubmitting(false)
        return false
      }

      if (selectedExport) {
        await updateExport(selectedExport.id, newExport)
        toast.info(`${newExport.name} updated`)
      } else {
        const exportItem = await addExport(newExport)
        toast.info(`${newExport.name} created`)
        history.push(`/exports/${exportItem.id}`)
      }
      onClose()
    } catch (error) {
      toast.error('An error occurred')
      setSubmitting(false)
      throw error
    }
  }

  const formsOptions = React.useMemo(() => {
    return forms?.map((form) => ({ value: form.id, label: form.name })) || []
  }, [forms])

  const missingQuestions = questions.filter(
    (q) => !newExport.questions.includes(q.id)
  )

  const onAddAll = () => {
    setNewExport((previous) => ({
      ...previous,
      questions: [...previous.questions, ...missingQuestions.map((q) => q.id)],
    }))
  }

  const onRemoveAll = () => {
    setNewExport((previous) => ({
      ...previous,
      questions: [],
    }))
  }

  return (
    <ModalDialog
      opened
      footer={
        <div className="my-4 flex justify-end px-4 sm:px-6">
          <PrimaryButton
            disabled={isSubmitting || isFormsLoading}
            onClick={onSave}
          >
            {(isSubmitting || isFormsLoading) && (
              <img alt="spinner" className="mr-2" src={spinner} />
            )}
            {selectedExport ? 'Save' : 'Create'}
          </PrimaryButton>
        </div>
      }
      onClose={onClose}
      title="Create Export"
    >
      <div className="mt-3 flex">
        <div className="mr-2 w-1/2">
          <label className="block text-sm font-medium leading-5 text-gray-700">
            Instructions:
          </label>
          <div className="text-xs text-gray-500">
            <div>1. Select the Form you want to Export</div>
            <div>2. Click Questions to add them to new export box</div>
            <div>3. Name Export</div>
            <div>4. Save</div>
          </div>
        </div>
        <div className="ml-2 w-1/2">
          <div className="mb-2">
            <SelectField
              semibold
              showPlaceholder
              disabled={isFormsLoading}
              label="Select Form"
              onChange={(e) => {
                const value = e.target.value
                setNewExport((previous) => ({
                  ...previous,
                  form: value,
                  questions: [],
                }))
              }}
              options={formsOptions}
              placeholder="Select Form"
              value={newExport.form || ''}
            />
          </div>
          <div>
            <TextField
              useEvent
              errorLabel={formErrorLabel?.name}
              hasError={formError?.name}
              label="New Export Name"
              name="exportname"
              onChange={(e) => {
                const value = e.target.value
                setNewExport((previous) => ({ ...previous, name: value }))
              }}
              placeholder="New Export"
              value={newExport.name || ''}
            />
          </div>
        </div>
      </div>
      <div className="mb-2 mt-4">
        <div className="flex flex-1">
          <div className="relative mr-2 w-1/2 rounded border bg-white shadow-sm">
            <div className="flex w-auto flex-shrink-0 justify-between border-b border-gray-200 bg-gray-50 px-3 py-3 text-left font-medium text-gray-500">
              Questions
              {missingQuestions.length > 0 && (
                <button onClick={onAddAll}>
                  <Icons.ChevronDoubleRight size={4} />
                </button>
              )}
            </div>
            <div className="flex flex-col">
              {isLoadingQuestions && (
                <div className="flex justify-center bg-white py-3 text-sm text-gray-700">
                  <Spinner className="mr-2" color="onLight" />
                  Loading
                </div>
              )}
              {map(
                questions.filter((q) => !newExport.questions.includes(q.id)),
                (question) => (
                  <button
                    className="flex items-center border-b border-gray-200 px-3 py-2"
                    key={question.id}
                    onClick={() => {
                      setNewExport((previous) => ({
                        ...previous,
                        questions: [...previous.questions, question.id],
                      }))
                    }}
                  >
                    <p className="flex flex-1 text-left text-sm font-medium leading-5 text-gray-700">
                      {question.text || 'Untitled'}
                    </p>
                    <div className="ml-3">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          clipRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          fillRule="evenodd"
                        />
                      </svg>
                    </div>
                  </button>
                )
              )}
            </div>
          </div>

          <div className="relative ml-2 w-1/2 rounded border bg-white shadow-sm">
            <div className="flex w-full flex-shrink-0 justify-between border-b border-gray-200 bg-gray-50 py-3 pl-3 pr-4 text-left font-medium text-gray-500">
              {newExport.name || 'New Export'}
              {newExport.questions.length > 0 && (
                <button onClick={onRemoveAll}>
                  <Icons.CloseCircle size={4} />
                </button>
              )}
            </div>
            {isLoadingQuestions && (
              <div className="flex justify-center bg-white py-3 text-sm text-gray-700">
                <Spinner className="mr-2" color="onLight" />
                Loading
              </div>
            )}
            {map(
              questions?.filter((q) => newExport.questions.includes(q.id)),
              (item, index) => (
                <div
                  className="flex items-center border-b border-gray-200 px-3 py-2"
                  key={String(index)}
                >
                  <p className="flex flex-1 text-left text-sm font-medium leading-5 text-gray-700">
                    {item.text || 'Untitled'}
                  </p>
                  <div className="ml-3">
                    <button
                      className="!outline-none"
                      onClick={() => {
                        setNewExport((previous) => ({
                          ...previous,
                          questions: previous.questions.filter(
                            (q) => q !== item.id
                          ),
                        }))
                      }}
                    >
                      <svg
                        className="float-right h-4 w-8"
                        height="512"
                        viewBox="0 0 512 512"
                        width="512"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192 192-86 192-192z"
                          fill="none"
                          stroke="#6b7280"
                          strokeMiterlimit="10"
                          strokeWidth="32"
                        />
                        <path
                          d="M320 320L192 192"
                          fill="none"
                          stroke="#6b7280"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="32"
                        />
                        <path
                          d="M192 320L320 192"
                          fill="none"
                          stroke="#6b7280"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="32"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
        {formError?.questions && (
          <ErrorLabel>{formErrorLabel?.questions}</ErrorLabel>
        )}
      </div>
    </ModalDialog>
  )
}
